import { useState } from 'react';

import {
  type ControlledVocabRow,
  type VocabCreatePayload,
  type VocabUpdatePayload,
  useCreateVocabRow,
  useUpdateVocabRow,
  useVocabAudit,
} from '../../hooks/useVocabularies';
import styles from './VocabulariesPage.module.css';

interface EditModalProps {
  /** When null → create-new mode. When a row → edit mode. */
  initial: ControlledVocabRow | null;
  /** Required when initial is null. Lets the modal scope the new row to
   *  the currently-selected vocab_key from the sidebar. */
  defaultVocabKey?: string;
  onClose: () => void;
}

/**
 * Phase A1 — admin edit modal for controlled_vocabulary rows.
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §9.5.
 *
 * Handles both create (initial=null) and update (initial=row). Attrs
 * are edited as freeform key/value pairs; deleting a key on save sends
 * `null` for that key (clears it server-side). The audit log for the
 * edited row is surfaced inline so admins can see prior edits before
 * committing a new one.
 */
export function VocabulariesEditModal({
  initial,
  defaultVocabKey,
  onClose,
}: EditModalProps) {
  const isCreate = initial === null;
  const [vocabKey, setVocabKey] = useState(initial?.vocab_key ?? defaultVocabKey ?? '');
  const [value, setValue] = useState(initial?.value ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [descTerse, setDescTerse] = useState(initial?.description_terse ?? '');
  const [descHuman, setDescHuman] = useState(initial?.description_human ?? '');
  const [examples, setExamples] = useState(initial?.prompt_examples ?? '');
  const [predicate, setPredicate] = useState(initial?.predicate_text ?? '');
  const [displayOrder, setDisplayOrder] = useState(initial?.display_order ?? 0);
  const [active, setActive] = useState(initial?.active ?? true);
  const [attrs, setAttrs] = useState<Array<{ key: string; value: string }>>(
    () => Object.entries(initial?.attrs ?? {}).map(([k, v]) => ({ key: k, value: v })),
  );
  // Track which initial attrs were deleted, so the PATCH payload can
  // signal `null` for them.
  const initialAttrKeys = Object.keys(initial?.attrs ?? {});

  const createMut = useCreateVocabRow();
  const updateMut = useUpdateVocabRow();
  const audit = useVocabAudit(initial?.vocab_key ?? null);
  const [error, setError] = useState<string | null>(null);

  function _emptyToUndefined(s: string): string | undefined {
    const trimmed = s.trim();
    return trimmed === '' ? undefined : trimmed;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Filter empty attr rows; build attr map.
    const attrsMap: Record<string, string> = {};
    for (const a of attrs) {
      const k = a.key.trim();
      const v = a.value.trim();
      if (k && v) attrsMap[k] = v;
    }

    try {
      if (isCreate) {
        const payload: VocabCreatePayload = {
          vocab_key: vocabKey.trim(),
          value: value.trim(),
          label: _emptyToUndefined(label),
          description_terse: _emptyToUndefined(descTerse),
          description_human: _emptyToUndefined(descHuman),
          prompt_examples: _emptyToUndefined(examples),
          predicate_text: _emptyToUndefined(predicate),
          display_order: displayOrder,
          attrs: attrsMap,
        };
        if (!payload.vocab_key || !payload.value) {
          setError('vocab_key and value are required');
          return;
        }
        await createMut.mutateAsync(payload);
      } else {
        // Build attrs merge: include all current attrs (upsert), plus
        // `null` for any key that was in `initial.attrs` but isn't now
        // (clear).
        const mergedAttrs: Record<string, string | null> = { ...attrsMap };
        for (const k of initialAttrKeys) {
          if (!(k in attrsMap)) mergedAttrs[k] = null;
        }
        const payload: VocabUpdatePayload = {
          label: label,
          description_terse: descTerse,
          description_human: descHuman,
          prompt_examples: examples,
          predicate_text: predicate,
          display_order: displayOrder,
          active,
          attrs: mergedAttrs,
        };
        await updateMut.mutateAsync({ vocabId: initial!.id, payload });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>
          {isCreate ? 'Add vocabulary value' : `Edit · ${initial!.vocab_key} / ${initial!.value}`}
        </h3>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <FormField label="vocab_key" required disabled={!isCreate}>
            <input
              type="text"
              className={styles.modalInput}
              value={vocabKey}
              onChange={(e) => setVocabKey(e.target.value)}
              disabled={!isCreate}
              placeholder="org_type"
              required
            />
          </FormField>

          <FormField label="value" required disabled={!isCreate}>
            <input
              type="text"
              className={styles.modalInput}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={!isCreate}
              placeholder="investment_bank"
              required
            />
          </FormField>

          <FormField label="label">
            <input
              type="text"
              className={styles.modalInput}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="(defaults to value if empty)"
            />
          </FormField>

          <FormField label="description (terse)" hint="Haiku-shaped, 1-2 sentences. Drives prompt injection.">
            <textarea
              className={styles.modalTextarea}
              value={descTerse}
              onChange={(e) => setDescTerse(e.target.value)}
              rows={2}
            />
          </FormField>

          <FormField label="description (human)" hint="Editor context — nuance, edge cases.">
            <textarea
              className={styles.modalTextarea}
              value={descHuman}
              onChange={(e) => setDescHuman(e.target.value)}
              rows={3}
            />
          </FormField>

          <FormField label="prompt examples" hint="Concrete anchors, e.g. 'Apollo, KKR, Blackstone'">
            <input
              type="text"
              className={styles.modalInput}
              value={examples}
              onChange={(e) => setExamples(e.target.value)}
            />
          </FormField>

          <FormField label="predicate" hint="For stage values: strict yes/no test that must be TRUE.">
            <textarea
              className={styles.modalTextarea}
              value={predicate}
              onChange={(e) => setPredicate(e.target.value)}
              rows={2}
            />
          </FormField>

          <div className={styles.modalRow}>
            <FormField label="display_order">
              <input
                type="number"
                className={styles.modalInput}
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
              />
            </FormField>
            {!isCreate && (
              <FormField label="active">
                <label className={styles.modalCheckboxLabel}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  <span>{active ? 'Active' : 'Soft-deleted (active=0)'}</span>
                </label>
              </FormField>
            )}
          </div>

          <FormField label="attrs" hint="Custom per-row attributes (e.g. role_group=intermediary for org_type rows).">
            <AttrsEditor attrs={attrs} setAttrs={setAttrs} />
          </FormField>

          {error && <div className={styles.modalError}>{error}</div>}

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.modalCancel}
              onClick={onClose}
              disabled={createMut.isPending || updateMut.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.modalSubmit}
              disabled={createMut.isPending || updateMut.isPending}
            >
              {createMut.isPending || updateMut.isPending
                ? 'Saving…'
                : isCreate
                ? 'Create'
                : 'Save changes'}
            </button>
          </div>
        </form>

        {!isCreate && audit.data && audit.data.rows.length > 0 && (
          <div className={styles.modalAuditSection}>
            <h4 className={styles.modalAuditTitle}>Recent edits</h4>
            <ul className={styles.modalAuditList}>
              {audit.data.rows
                .filter((r) => r.vocab_id === initial!.id)
                .slice(0, 8)
                .map((r) => (
                  <li key={r.id}>
                    <span className={styles.modalAuditWhen}>
                      {new Date(r.edited_at).toLocaleString()}
                    </span>
                    <span className={styles.modalAuditAction}>{r.action}</span>
                    {r.edited_by_email && (
                      <span className={styles.modalAuditWho}>by {r.edited_by_email}</span>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function FormField({
  label,
  hint,
  required,
  disabled,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.modalField}>
      <label
        className={`${styles.modalLabel} ${disabled ? styles.modalLabelDisabled : ''}`}
      >
        {label}
        {required && <span className={styles.modalRequired}> *</span>}
      </label>
      {children}
      {hint && <div className={styles.modalHint}>{hint}</div>}
    </div>
  );
}

function AttrsEditor({
  attrs,
  setAttrs,
}: {
  attrs: Array<{ key: string; value: string }>;
  setAttrs: (next: Array<{ key: string; value: string }>) => void;
}) {
  return (
    <div className={styles.attrsEditor}>
      {attrs.length === 0 && (
        <div className={styles.modalHint}>No attrs set. Click + to add.</div>
      )}
      {attrs.map((a, i) => (
        <div key={i} className={styles.attrsRow}>
          <input
            type="text"
            className={styles.modalInput}
            value={a.key}
            placeholder="attr_key (e.g. role_group)"
            onChange={(e) => {
              const next = [...attrs];
              next[i] = { ...next[i], key: e.target.value };
              setAttrs(next);
            }}
          />
          <input
            type="text"
            className={styles.modalInput}
            value={a.value}
            placeholder="attr_value (e.g. intermediary)"
            onChange={(e) => {
              const next = [...attrs];
              next[i] = { ...next[i], value: e.target.value };
              setAttrs(next);
            }}
          />
          <button
            type="button"
            className={styles.attrsRemoveBtn}
            onClick={() => setAttrs(attrs.filter((_, j) => j !== i))}
            aria-label="Remove attr"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className={styles.attrsAddBtn}
        onClick={() => setAttrs([...attrs, { key: '', value: '' }])}
      >
        + add attr
      </button>
    </div>
  );
}
